import React, { useEffect, useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'
import { invoiceAdvanceDenormalizedEntries } from 'provider/queries/hasuraQueries'
import { Datagrid, Error, useQuery, Loading } from 'react-admin'
import { useSelector } from 'react-redux'
import { post as getFromHasura } from 'utils/axios'

import { GOOGLE_CLOUD_STORAGE_URL } from '../../configs/api'
import tracker from '../../utils/tracker'
import { PaymentEmpty } from '../emptyViews/paymentEmptyView'
import { EventsPaymentExpandableView } from '../expandableViews/eventsPaymentExpandableView'
import { CurrencyField } from '../fields/currencyField'
import { FileField } from '../fields/fileField'
import { LocalDateField } from '../fields/localDateField'
import { PaymentDescriptionField } from '../fields/paymentDescriptionField'
import { PaymentPayerField } from '../fields/paymentPayerField'
import { CustomDatagridBody } from './customDatagridBody'
import { CustomDatagridRow } from './customDatagridRow'

const SORT = {
  field: 'expires_at',
  order: 'ASC',
}

const useStyles = makeStyles((theme) => ({
  date: {
    whiteSpace: 'nowrap',
    paddingRight: '40px',
  },
  headerRightWithoutIcon: {
    textAlign: 'right',
    paddingRight: '100px',
  },
  description: {},
  scrollPanelWithSidebarClosed: {
    display: 'flex',
    overflowY: 'auto',
    overflowX: 'auto',
    width: 'calc(100vw - 200px)',
    maxHeight: 'calc(95vh - 250px)',
  },
  scrollPanelWithSidebarOpened: {
    display: 'flex',
    overflowY: 'auto',
    overflowX: 'auto',
    width: 'calc(100vw - 300px)',
    maxHeight: 'calc(95vh - 250px)',
  },
  [theme.breakpoints.down('sm')]: {
    scrollPanel: {
      maxWidth: '80vw',
    },
  },
}))

function mapEntriesById(entries) {
  const entriesObj = {}

  for (const entry of entries) {
    const enhancedEntry = {
      ...entry,
      invoiceId: entry.invoice?.id,
      date: entry.Date,
      directionalTransaction: entry.directional_direction,
      type: entry.Type,
    }

    if (entriesObj[entry.reference]?.data) {
      entriesObj[entry.reference].data.push(enhancedEntry)
    } else {
      entriesObj[entry.reference] = { data: [] }
      entriesObj[entry.reference].data.push(enhancedEntry)
    }
  }

  return entriesObj
}

function makeFileURL(receipt) {
  return `${GOOGLE_CLOUD_STORAGE_URL}/${receipt}`
}

function normalizeWireConfirmationData(data) {
  return (
    data.receipt && [
      {
        url: makeFileURL(data.receipt),
        type: 'application/pdf',
        filename: data.wireConfirmation,
      },
    ]
  )
}

function mergePropsWithEntries(props, entries) {
  let newProps = props

  const dataKeys = Object.keys(newProps.data)
  for (let i = 0; i < dataKeys.length; i++) {
    newProps.data[dataKeys[i]].entries = []

    // normalize wireConfirmation file container
    newProps.data[dataKeys[i]].wireConfirmation = normalizeWireConfirmationData(
      newProps.data[dataKeys[i]]
    )
  }

  const keys = Object.keys(entries)

  for (let i = 0; i < keys.length; i++) {
    if (newProps.data[keys[i]]) {
      newProps.data[keys[i]].entryIds = [
        ...entries[keys[i]].data.map((entry) => entry.invoice_advance_id),
      ]

      newProps.data[keys[i]].entries = entries[keys[i]].data
    }
  }

  return newProps
}

export const PaymentDatagrid = (props) => {
  const [enhancedProps, setEnhancedProps] = useState()
  const classes = useStyles()
  const open = useSelector((state) => state.admin.ui.sidebarOpen)

  const { data, error } = useQuery({
    type: 'getList',
    resource: 'creditlines',
    payload: {
      pagination: { page: 1, perPage: 0 },
      sort: SORT,
    },
  })

  const { total, loading } = props

  useEffect(() => {
    if (props.onChangeList)
      props.onChangeList({
        total,
        loading,
      })
  }, [total, loading, props])

  useEffect(() => {
    async function fetchEntries(invoicesIds) {
      const entriesRes = await getFromHasura({
        payload: invoiceAdvanceDenormalizedEntries('reference'),
        variables: {
          reference: invoicesIds,
        },
      })

      if (entriesRes.errors?.length) {
        console.error(JSON.stringify(entriesRes.errors[0]))
        return
      }

      const mappedEntries = mapEntriesById(
        entriesRes.data.invoiceadvance_view_denormalized_entries
      )

      const propsWithEntries = mergePropsWithEntries(props, mappedEntries)

      setEnhancedProps(propsWithEntries)
    }

    // get ids to fetch entries
    const invoicesIds = Object.keys(props.data)
    // fetch entries
    fetchEntries(invoicesIds)
    // attach entries to invoice id
  }, [props, props.data])

  if (!total && !loading) {
    return <PaymentEmpty />
  }

  if (error) return <Error error={error} />

  if (props.loading) return <Loading />

  const handleFileClick = () => {
    tracker.event({
      action: `${
        props.resource ?? 'payments'
      }_transfer_receipt_clicked_clicked`,
      category: `${props.resource ?? 'payments'}`,
    })
  }

  const handleUserExpand = (props) => {
    tracker.event({
      action: `payment_details_expanded`,
      category: `payments`,
      label: props.record ? props.record.reference : undefined,
    })
  }

  const handleUserCollapse = (props) => {
    tracker.event({
      action: `payment_details_collapsed`,
      category: `payments`,
      label: props.record ? props.record.reference : undefined,
    })
  }

  return (
    <div
      className={
        open
          ? classes.scrollPanelWithSidebarOpened
          : classes.scrollPanelWithSidebarClosed
      }
    >
      <Datagrid
        {...(enhancedProps ? enhancedProps : props)}
        expand={<EventsPaymentExpandableView />}
        body={
          <CustomDatagridBody
            {...(enhancedProps ? enhancedProps : props)}
            row={
              <CustomDatagridRow
                {...(enhancedProps ? enhancedProps : props)}
                onUserExpand={handleUserExpand}
                onUserCollapse={handleUserCollapse}
              />
            }
          />
        }
      >
        <LocalDateField
          source="completedAt"
          label={'resources.payments.payment.date'}
        />
        <PaymentPayerField
          source="type"
          sourceImporterName="importerName"
          sourceCreditLine="creditLine"
          sourceType="Disbursement"
          label={'resources.payments.payment.importerName'}
          linkDataCreditLines={data}
        />
        <PaymentDescriptionField
          source="concept"
          label={'resources.payments.payment.description'}
          className={classes.description}
        />
        <CurrencyField
          source="totalEntries"
          label={'resources.payments.payment.amount'}
          className={classes.date}
          headerClassName={classes.headerRightWithoutIcon}
        />
        <FileField
          source="wireConfirmation"
          label={'resources.payments.payment.notes'}
          handleClick={handleFileClick}
        />
      </Datagrid>
    </div>
  )
}
